import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  GridItem,
  Flex,
  Image,
  Card,
  Text,
  Spinner,
  Radio,
  RadioGroup,
  Stack,
  Button,
  useToast,
} from "@chakra-ui/react";

import { BackGroundUI } from "../../components";

import HeroImage from "../../assets/Hero.webp";
import LogoImage from "../../assets/logo.svg";

import { useNavigate, useSearchParams } from "react-router-dom";
import { UsersService } from "../../services";

const STATUS = {
  LOADING: "LOADING",
  READY: "READY",
  SAVING: "SAVING",
  SAVED: "SAVED",
  ERROR: "ERROR",
};

export default function UnSubscribe() {
  const navigate = useNavigate();
  const toast = useToast();

  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const sig = searchParams.get("sig");

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [serverSubscribed, setServerSubscribed] = useState(true);
  const [selection, setSelection] = useState("subscribed");

  useEffect(() => {
    const load = async () => {
      if (!email || !sig) {
        setErrorMessage("This unsubscribe link is invalid or incomplete.");
        setStatus(STATUS.ERROR);
        return;
      }
      try {
        const data = await UsersService.getEmailPreferences({ email, sig });
        setServerSubscribed(!!data.subscribed);
        setSelection(data.subscribed ? "subscribed" : "unsubscribed");
        setStatus(STATUS.READY);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "We couldn't load your preferences. The link may be invalid or expired.";
        setErrorMessage(message);
        setStatus(STATUS.ERROR);
      }
    };
    load();
  }, [email, sig]);

  const onSave = async () => {
    setStatus(STATUS.SAVING);
    try {
      const subscribed = selection === "subscribed";
      await UsersService.setEmailPreferences({ email, sig, subscribed });
      setServerSubscribed(subscribed);
      setStatus(STATUS.SAVED);
      toast({
        title: subscribed
          ? "You'll keep receiving Purchr emails."
          : "You've been unsubscribed from all Purchr emails.",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Couldn't save your preferences. Please try again.";
      setErrorMessage(message);
      setStatus(STATUS.ERROR);
    }
  };

  const isDirty = (selection === "subscribed") !== serverSubscribed;
  const showForm = status === STATUS.READY || status === STATUS.SAVING || status === STATUS.SAVED;

  return (
    <Box
      bg="purchr.bgHero"
      h={`${window.innerHeight}px`}
      p={{ base: "0", sm: "0px", xl: "0 40px 0 40px" }}
    >
      <BackGroundUI />
      <Grid
        h="100%"
        templateColumns="repeat(10, 1fr)"
        gap="16px"
        alignItems="center"
      >
        <GridItem colSpan={{ base: 10, xl: 5, "2xl": 4 }}>
          <Box>
            <Flex h="100%" direction="column" alignItems="center">
              <Image src={LogoImage} height="100px" mb="24px" zIndex={1} />
              <Card
                p={{ base: "48px", sm: "60px 60px" }}
                borderRadius="42px"
                w={{ base: "auto", sm: "544px" }}
              >
                {status === STATUS.LOADING && (
                  <Flex justifyContent="center" alignItems="center">
                    <Spinner size="md" />
                  </Flex>
                )}

                {status === STATUS.ERROR && (
                  <Flex direction="column" gap="16px">
                    <Text fontSize="22px" fontWeight="bold" color="purchr.textPrimary">
                      Something went wrong
                    </Text>
                    <Text color="purchr.textPrimary">{errorMessage}</Text>
                    <Button onClick={() => navigate("/")} mt="8px">
                      Go to Purchr
                    </Button>
                  </Flex>
                )}

                {showForm && (
                  <Flex direction="column" gap="20px">
                    <Text fontSize="24px" fontWeight="bold" color="purchr.textPrimary">
                      Email preferences
                    </Text>
                    <Text color="purchr.textPrimary" fontSize="14px">
                      Manage email preferences for{" "}
                      <Text as="span" fontWeight="bold">
                        {email}
                      </Text>
                      .
                    </Text>

                    <RadioGroup value={selection} onChange={setSelection}>
                      <Stack spacing="12px">
                        <Radio value="subscribed">
                          Receive emails from Purchr
                        </Radio>
                        <Radio value="unsubscribed">
                          Unsubscribe from all emails
                        </Radio>
                      </Stack>
                    </RadioGroup>

                    <Flex gap="12px" mt="8px">
                      <Button
                        onClick={onSave}
                        isLoading={status === STATUS.SAVING}
                        isDisabled={!isDirty || status === STATUS.SAVING}
                        colorScheme="orange"
                      >
                        Save preferences
                      </Button>
                      <Button variant="ghost" onClick={() => navigate("/")}>
                        Cancel
                      </Button>
                    </Flex>

                    {status === STATUS.SAVED && (
                      <Text color="green.600" fontSize="14px">
                        {serverSubscribed
                          ? "Saved — you'll keep receiving Purchr emails."
                          : "Saved — you've been unsubscribed from all Purchr emails. You can re-enable any time from your in-app settings or this page."}
                      </Text>
                    )}
                  </Flex>
                )}
              </Card>
            </Flex>
          </Box>
        </GridItem>
        <GridItem
          colSpan={{ base: 0, xl: 5, "2xl": 6 }}
          display={{ base: "none", xl: "block" }}
          alignSelf="start"
          mt="60px"
        >
          <Box>
            <Flex h={"100%"} direction={"column"}>
              <Text
                mt="24px"
                maxW="397px"
                as="span"
                alignSelf="center"
                color="purchr.blue.dark"
                fontWeight="400"
                fontSize="24px"
              >
                Alerting you to
                <Text
                  as="span"
                  color="purchr.brown.dark"
                  fontWeight="700"
                  fontSize="24px"
                  ml="6px"
                >
                  price drops
                </Text>{" "}
                on your favourite items, everywhere.
              </Text>
              <Box h={`calc(${window.innerHeight}px - 156px)`}>
                <Flex h="100%" alignItems="center" justifyContent="center">
                  <Image src={HeroImage} zIndex="1" />
                </Flex>
              </Box>
            </Flex>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}
